import React, { useState, useEffect, useCallback } from 'react';
import {
  Upload, Brain, FileText, CheckCircle2, Trash2,
  Loader, DollarSign, Droplet, History,
  Zap, Wind, Users, Package, FileCheck, Database,
  ChevronRight, RefreshCw,
  FileSpreadsheet, FileImage, File,
  ArrowRight, Layers, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { apiFetch } from '../services/api_service';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons
const FILE_ICONS = {
  pdf: FileText,
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
  csv: FileSpreadsheet,
  doc: FileText,
  docx: FileText,
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  default: File
};

// Data source types for manual entry
const DATA_SOURCE_TYPES = [
  { value: 'emissions_data', label: 'Emissions Data', icon: Wind, color: 'text-orange-600', bg: 'bg-orange-100', description: 'Scope 1, 2, 3 GHG emissions' },
  { value: 'energy_consumption', label: 'Energy Consumption', icon: Zap, color: 'text-yellow-600', bg: 'bg-yellow-100', description: 'Electricity, gas, renewable energy' },
  { value: 'financial_data', label: 'Financial Data', icon: DollarSign, color: 'text-blue-600', bg: 'bg-blue-100', description: 'Revenue, costs, financial KPIs' },
  { value: 'water_usage', label: 'Water Usage', icon: Droplet, color: 'text-cyan-600', bg: 'bg-cyan-100', description: 'Water consumption, wastewater' },
  { value: 'waste_management', label: 'Waste Management', icon: Package, color: 'text-red-600', bg: 'bg-red-100', description: 'Waste generation, recycling' },
  { value: 'employee_data', label: 'Employee Data', icon: Users, color: 'text-purple-600', bg: 'bg-purple-100', description: 'Workforce demographics, safety' },
  { value: 'supply_chain', label: 'Supply Chain', icon: Building2, color: 'text-indigo-600', bg: 'bg-indigo-100', description: 'Supplier data, procurement' },
  { value: 'compliance_data', label: 'Compliance', icon: FileCheck, color: 'text-pink-600', bg: 'bg-pink-100', description: 'Audits, certifications' },
  { value: 'other', label: 'Other', icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100', description: 'Other data sources' }
];

// Manual Upload Item Card (for legacy uploads)
const ManualUploadCard = ({ item, onDelete, onAnalyze }) => {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <FileSpreadsheet className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="font-medium text-sm">{item.filename}</p>
              <p className="text-xs text-gray-500">{item.data_source_type}</p>
            </div>
          </div>
          <Badge variant="outline">{item.format}</Badge>
        </div>
        
        {item.row_count && (
          <p className="text-xs text-gray-400 mt-2">{item.row_count} rows</p>
        )}
        
        <div className="mt-3 pt-3 border-t flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={() => onAnalyze(item.filename)}>
            <Brain className="h-4 w-4 mr-1" />
            Analyze
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onDelete(item.filename)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ESRS Column Mapping Tab (gap 2: uploads → module datapoints)
const EsrsMappingTab = () => {
  const [file, setFile] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestResult, setSuggestResult] = useState(null);
  const [datasetKind, setDatasetKind] = useState('emissions');
  const [mapping, setMapping] = useState({});
  const [reportingPeriod, setReportingPeriod] = useState(String(new Date().getFullYear()));
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const handleFile = async (picked) => {
    if (!picked) return;
    setFile(picked);
    setApplyResult(null);
    setSuggesting(true);
    try {
      const formData = new FormData();
      formData.append('file', picked);
      const result = await apiFetch('/mapping/suggest', { method: 'POST', body: formData });
      if (result._error) {
        toast.error(result.message || 'Could not read CSV headers');
        setSuggestResult(null);
      } else {
        setSuggestResult(result);
        setDatasetKind(result.dataset_kind || 'emissions');
        const prefill = {};
        (result.suggestions || []).forEach((s) => {
          if (s.source_field) prefill[s.header] = s.source_field;
        });
        setMapping(prefill);
      }
    } finally {
      setSuggesting(false);
    }
  };

  const handleApply = async () => {
    const active = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
    if (!Object.keys(active).length) {
      toast.error('Map at least one column before applying');
      return;
    }
    setApplying(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(active));
      formData.append('dataset_kind', datasetKind);
      formData.append('reporting_period', reportingPeriod);
      const result = await apiFetch('/mapping/apply', { method: 'POST', body: formData });
      if (result._error) {
        toast.error(result.message || 'Mapping failed');
      } else {
        setApplyResult(result);
        toast.success(`${result.datapoints_written} ESRS datapoints written from ${result.rows_processed} rows`);
      }
    } finally {
      setApplying(false);
    }
  };

  const fields = suggestResult?.available_fields?.[datasetKind] || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Map columns to ESRS datapoints</CardTitle>
        <CardDescription>
          Upload a CSV of emissions or workforce totals, confirm which column feeds which
          ESRS datapoint, and the values land in your E1/S1 disclosures with evidence lineage.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">CSV file</label>
            <Input type="file" accept=".csv" data-testid="mapping-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])} />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Dataset kind</label>
            <select value={datasetKind} data-testid="mapping-kind-select"
              onChange={(e) => { setDatasetKind(e.target.value); setMapping({}); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm">
              <option value="emissions">Emissions (ESRS E1)</option>
              <option value="workforce">Workforce (ESRS S1)</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Reporting period</label>
            <Input value={reportingPeriod} onChange={(e) => setReportingPeriod(e.target.value)}
              className="w-28 font-mono" />
          </div>
        </div>

        {suggesting && (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Loader className="h-4 w-4 animate-spin" /> Reading headers…
          </p>
        )}

        {suggestResult && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              <span className="font-mono">{suggestResult.row_count}</span> data rows ·
              detected kind: <span className="font-mono">{suggestResult.dataset_kind || 'unknown'}</span>
            </p>
            <div className="border rounded-lg divide-y">
              {suggestResult.headers.map((header) => {
                const suggestion = (suggestResult.suggestions || []).find((s) => s.header === header);
                return (
                  <div key={header} className="flex flex-wrap items-center gap-3 p-3">
                    <span className="font-mono text-sm w-56 truncate" title={header}>{header}</span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                    <select value={mapping[header] || ''}
                      onChange={(e) => setMapping({ ...mapping, [header]: e.target.value })}
                      className="h-9 flex-1 min-w-52 rounded-md border border-input bg-background px-3 text-sm">
                      <option value="">— not mapped —</option>
                      {fields.map((f) => (
                        <option key={f.source_field} value={f.source_field}>
                          {f.esrs_id} · {f.esrs_name}
                        </option>
                      ))}
                    </select>
                    {suggestion?.source_field && suggestion.tier !== 'none' && (
                      <Badge variant="secondary" className="font-mono">
                        {suggestion.tier} {Math.round(suggestion.confidence * 100)}%
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
            <Button onClick={handleApply} disabled={applying} data-testid="mapping-apply-btn">
              {applying ? <Loader className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              Write to ESRS datapoints
            </Button>
          </div>
        )}

        {applyResult && (
          <div className="border rounded-lg p-4 space-y-2 bg-emerald-50/50">
            <p className="text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span className="font-mono">{applyResult.datapoints_written}</span> datapoints written
              (period <span className="font-mono">{applyResult.reporting_period}</span>)
            </p>
            {(applyResult.datapoints || []).map((dp) => (
              <p key={dp.esrs_id} className="text-xs text-gray-600">
                <span className="font-mono">{dp.esrs_id}</span> {dp.name}:{' '}
                <span className="font-mono">{dp.value} {dp.unit}</span>
              </p>
            ))}
            {applyResult.manually_overridden_kept > 0 && (
              <p className="text-xs text-amber-700">
                {applyResult.manually_overridden_kept} datapoint(s) kept their manual override.
              </p>
            )}
            {(applyResult.skipped || []).length > 0 && (
              <details className="text-xs text-gray-500">
                <summary>{applyResult.skipped.length} cell(s) skipped</summary>
                {applyResult.skipped.slice(0, 20).map((s, i) => (
                  <p key={i} className="font-mono">row {s.row} · {s.column}: {s.reason}</p>
                ))}
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Component
const UnifiedDataUpload = () => {
  const [activeTab, setActiveTab] = useState('esrs-mapping');
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Manual Entry state
  const [selectedType, setSelectedType] = useState('');
  const [description, setDescription] = useState('');
  const [manualUploads, setManualUploads] = useState([]);
  
  // Fetch uploads on mount
  useEffect(() => {
    fetchManualUploads();
  }, []);

  const fetchManualUploads = async () => {
    const data = await apiFetch(`${API_URL}/api/data/uploads`);
    if (data?._error) {
      console.error('Error fetching manual uploads:', data.message);
      return;
    }
    setManualUploads(data.uploads || []);
  };

  // Manual Entry Upload Handler
  const handleManualUpload = async (files) => {
    if (!selectedType) {
      toast.error('Please select a data type first');
      return;
    }
    
    setLoading(true);

    const file = files[0];

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(',')[1];

      const data = await apiFetch(`${API_URL}/api/data/upload`, {
        method: 'POST',
        body: {
          filename: file.name,
          data_source_type: selectedType,
          content_base64: base64,
          description: description,
          route_to_modules: true
        }
      });

      if (data?._error) {
        toast.error(data.message || 'Upload failed');
        return;
      }

      toast.success(`Uploaded ${file.name}!`);
      setSelectedType('');
      setDescription('');
      fetchManualUploads();
    };

    reader.readAsDataURL(file);
    setLoading(false);
  };

  // Drag handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleManualUpload(e.dataTransfer.files);
    }
  }, [selectedType, description]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleManualUpload(e.target.files);
    }
  };

  const handleDeleteManual = async (filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    
    const data = await apiFetch(`${API_URL}/api/data/uploads/${filename}`, { method: 'DELETE' });
    if (data?._error) {
      toast.error('Failed to delete');
      return;
    }
    toast.success('File deleted');
    fetchManualUploads();
  };

  const handleAnalyze = async (filename) => {
    toast.info('Starting AI analysis...');
    const data = await apiFetch(`${API_URL}/api/data/analyze/${filename}`, { method: 'POST', body: {} });
    if (data?._error) {
      toast.error('Analysis failed');
      return;
    }
    toast.success('Analysis complete');
  };

  const totalUploads = manualUploads.length;

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="unified-data-upload">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Database className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Data Upload Center</h1>
              <p className="text-blue-100">Upload data files and map them to ESRS datapoints</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-3 w-full max-w-2xl">
          <TabsTrigger value="manual-entry" className="gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Manual Entry
          </TabsTrigger>
          <TabsTrigger value="esrs-mapping" className="gap-2" data-testid="esrs-mapping-tab">
            <Layers className="h-4 w-4" />
            ESRS Mapping
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            History
            {totalUploads > 0 && (
              <Badge variant="secondary" className="ml-1">{totalUploads}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Manual Entry Tab */}
        <TabsContent value="manual-entry">
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                    Manual Data Entry
                  </CardTitle>
                  <CardDescription>
                    Select a data type and upload your structured data file
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Data Type Selection */}
                  <div className="mb-6">
                    <p className="text-sm font-medium mb-3">1. Select Data Type</p>
                    <div className="grid grid-cols-3 gap-3">
                      {DATA_SOURCE_TYPES.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => setSelectedType(type.value)}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            selectedType === type.value
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`p-1.5 rounded ${type.bg}`}>
                              <type.icon className={`h-4 w-4 ${type.color}`} />
                            </div>
                            <span className="font-medium text-sm">{type.label}</span>
                          </div>
                          <p className="text-xs text-gray-500">{type.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-6">
                    <p className="text-sm font-medium mb-2">2. Description (optional)</p>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add notes about this data..."
                    />
                  </div>

                  {/* Upload Zone */}
                  <div className="mb-4">
                    <p className="text-sm font-medium mb-2">3. Upload File</p>
                    <div
                      className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                        !selectedType 
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                          : dragActive 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-300 hover:border-blue-400'
                      }`}
                      onDragEnter={selectedType ? handleDrag : undefined}
                      onDragLeave={selectedType ? handleDrag : undefined}
                      onDragOver={selectedType ? handleDrag : undefined}
                      onDrop={(e) => selectedType && handleDrop(e)}
                    >
                      {!selectedType ? (
                        <p className="text-gray-400">Select a data type first</p>
                      ) : loading ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader className="h-5 w-5 animate-spin text-blue-600" />
                          <span>Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600 mb-2">Drop file here or click to browse</p>
                          <input
                            type="file"
                            id="manual-file-input"
                            className="hidden"
                            accept=".xlsx,.xls,.csv"
                            onChange={(e) => handleFileInput(e)}
                          />
                          <Button variant="outline" size="sm" asChild>
                            <label htmlFor="manual-file-input" className="cursor-pointer">
                              Browse Files
                            </label>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Manual Uploads */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Recent Manual Uploads</CardTitle>
                </CardHeader>
                <CardContent>
                  {manualUploads.length > 0 ? (
                    <div className="space-y-3">
                      {manualUploads.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium truncate max-w-[150px]">{item.filename}</p>
                            <p className="text-xs text-gray-400">{item.data_source_type}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteManual(item.filename)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">No manual uploads yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ESRS Mapping Tab */}
        <TabsContent value="esrs-mapping">
          <EsrsMappingTab />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Upload History</CardTitle>
                  <CardDescription>All your uploaded documents and data files</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchManualUploads}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {totalUploads > 0 ? (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    Manual Uploads ({manualUploads.length})
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {manualUploads.map((item, idx) => (
                      <ManualUploadCard
                        key={idx}
                        item={item}
                        onDelete={handleDeleteManual}
                        onAnalyze={handleAnalyze}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Upload className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No uploads yet</p>
                  <p className="text-sm text-gray-400">Upload your first document to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UnifiedDataUpload;
